import { useState } from "react";
import { RotateCcw, Trash2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { ago, dt, nf } from "./format";
import { Btn, C, Input, LogoPlate, Modal, Pill, useToast } from "./ui";

/** Papierkorb: gelöschte Betriebe wiederherstellen oder endgültig entfernen. Erscheint nur, wenn etwas drin liegt. */
export function Trash() {
  const utils = trpc.useUtils();
  const toast = useToast();
  const q = trpc.platform.orgs.trash.useQuery(undefined, {
    // solange ein endgültiges Löschen läuft, regelmäßig nachsehen
    refetchInterval: (query) => (query.state.data?.some((o) => o.purge?.status === "running") ? 2500 : false),
  });
  const [target, setTarget] = useState<{ id: string; name: string } | null>(null);
  const [name, setName] = useState("");

  const refresh = () => {
    utils.platform.orgs.trash.invalidate();
    utils.platform.orgs.list.invalidate();
    utils.platform.overview.invalidate();
  };
  const onError = (e: { message: string }) => toast({ kind: "err", title: "Fehler", text: e.message });
  const restore = trpc.platform.orgs.restore.useMutation({
    onSuccess: () => {
      refresh();
      toast({ kind: "ok", title: "Betrieb wiederhergestellt" });
    },
    onError,
  });
  const purge = trpc.platform.orgs.purge.useMutation({
    onSuccess: () => {
      setTarget(null);
      refresh();
      toast({ kind: "ok", title: "Endgültiges Löschen gestartet", text: "Das kann bei großen Katalogen einige Minuten dauern." });
    },
    onError,
  });

  if (!q.data?.length) return null;

  return (
    <div className="pf-card" style={{ marginTop: 4 }}>
      <h3 className="pf-h">Papierkorb · {q.data.length}</h3>
      <div className="pf-list">
        {q.data.map((o) => (
          <li key={o.id} style={{ listStyle: "none" }}>
            <div className="pf-row" style={{ flexWrap: "wrap" }}>
              <LogoPlate url={o.logoUrl} name={o.legalName} size={40} wide />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{o.legalName}</div>
                <div className="pf-muted" style={{ fontSize: 12 }}>
                  gelöscht {ago(o.deletedAt)} · {dt(o.deletedAt)} · {nf(o.users)} Benutzer, {nf(o.customers)} Kunden, {nf(o.invoices)} Rechnungen, {nf(o.articles)} Artikel
                </div>
              </div>
              {o.purge?.status === "running" ? (
                <Pill color={C.amber} dot>
                  {o.purge.step}
                </Pill>
              ) : o.purge?.status === "error" ? (
                <Pill color={C.red}>Fehler: {o.purge.message}</Pill>
              ) : null}
              <Btn
                size="sm"
                disabled={o.purge?.status === "running"}
                loading={restore.isPending && restore.variables?.id === o.id}
                onClick={() => restore.mutate({ id: o.id })}
              >
                <RotateCcw size={14} /> Wiederherstellen
              </Btn>
              <Btn
                size="sm"
                variant="danger"
                disabled={o.purge?.status === "running"}
                onClick={() => {
                  setName("");
                  setTarget({ id: o.id, name: o.legalName });
                }}
              >
                <Trash2 size={14} /> Endgültig löschen
              </Btn>
            </div>
          </li>
        ))}
      </div>

      <Modal
        open={!!target}
        onClose={() => setTarget(null)}
        title="Endgültig löschen?"
        lead="Alle Daten dieses Betriebs werden unwiderruflich entfernt: Kunden, Angebote, Rechnungen, Artikel, Dateien (Logo, PDFs) und die Benutzer, die nur zu diesem Betrieb gehören. Das lässt sich nicht rückgängig machen."
        width={540}
        footer={
          <>
            <Btn variant="ghost" onClick={() => setTarget(null)}>
              Abbrechen
            </Btn>
            <Btn
              variant="danger"
              loading={purge.isPending}
              disabled={!target || name.trim().toLowerCase() !== target.name.trim().toLowerCase()}
              onClick={() => target && purge.mutate({ id: target.id, confirmName: name })}
            >
              Unwiderruflich löschen
            </Btn>
          </>
        }
      >
        <Input label={`Zur Bestätigung den Namen eintippen: ${target?.name ?? ""}`} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Modal>
    </div>
  );
}
